<template>
    <Head title="Bank Accounts"/>

    <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
        Bank Accounts
    </h1>
    <p class="text-sm  text-gray-500">Bank accounts are assigned to projects
        and displayed on invoices sent to clients.</p>

    <div class="bg-white py-6 px-4 mt-4 sm:p-6 shadow">
        <div class="flex justify-end">
            <search-filter :show-filter="false" v-model:value="form.search" class="w-1/4 max-w-md mr-4"
                           @reset="reset"></search-filter>
            <Button v-on:click="createBankAccountSlideout()"
                    class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amaze-coral">
                <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Add New Bank Account
            </Button>
        </div>
        <div class="flex flex-col mt-3 space-y-3">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                            <tr>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Bank Name
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Account Number
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Sort Code
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    IBAN
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    BIC
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    VAT Number
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Currency
                                </th>
                                <th scope="col" class="relative px-6 py-3">
                                    <span class="sr-only">Edit</span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="bank_accounts.data.length > 0" v-for="(bank_account, index) in bank_accounts.data" :key="index"
                                :class="{'bg-gray-100': index % 2}" class="hover:bg-gray-50 cursor-pointer">
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ bank_account.name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ bank_account.bank_name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ bank_account.account_number }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ bank_account.sort_code }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ bank_account.iban }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ bank_account.bic }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ bank_account.vat_number }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ bank_account.currency }}
                                </td>
                                <td class="px-6  py-4 whitespace-nowrap text-right text-sm flex">
                                    <PencilAltIcon @click="editBankAccountSlideout(bank_account)"
                                                   class="w-4 h-4 mr-3"></PencilAltIcon>
                                </td>
                            </tr>
                            <tr v-else>
                                <td colspan="8" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">
                                    There are no offices available for your search criteria.
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <Pagination :links="bank_accounts.links"/>
        </div>
    </div>

    <CreateBankAccount ref="createBankAccountSlideout"/>

</template>


<script>

import Authenticated from "@/Layouts/Authenticated.vue";
import {Head, useForm} from "@inertiajs/vue3";
import Pagination from "@/Components/Pagination.vue";
import SearchFilter from "@/Components/SearchFilter.vue";
import debounce from 'lodash/debounce'
import pickBy from 'lodash/pickBy'
import mapValues from 'lodash/mapValues'
import Button from "@/Components/Button.vue";
import CreateBankAccount from "@/Components/SlideOuts/CreateBankAccount.vue";
import {PencilAltIcon, TrashIcon} from "@heroicons/vue/solid";

export default {
    layout: Authenticated,
    components: {
        CreateBankAccount,
        Button,
        Pagination,
        Head,
        SearchFilter,
        PencilAltIcon,
        TrashIcon
    },
    props: {
        bank_accounts: Object
    },
    data() {
        return {
            form: {
                search: null
            }
        }
    },
    methods: {
        reset() {
            this.form = mapValues(this.form, () => null)
        },
        createBankAccountSlideout() {
            this.$refs.createBankAccountSlideout.updating = false
            this.$refs.createBankAccountSlideout.open();
        },
        editBankAccountSlideout(bank_account = null) {
            this.$refs.createBankAccountSlideout.updating = bank_account !== null;
            this.$refs.createBankAccountSlideout.form.id = bank_account.id ?? null;
            this.$refs.createBankAccountSlideout.form.name = bank_account.name ?? null;
            this.$refs.createBankAccountSlideout.form.bank_name = bank_account.bank_name ?? null;
            this.$refs.createBankAccountSlideout.form.account_number = bank_account.account_number ?? null;
            this.$refs.createBankAccountSlideout.form.sort_code = bank_account.sort_code ?? null;
            this.$refs.createBankAccountSlideout.form.bic = bank_account.bic ?? null;
            this.$refs.createBankAccountSlideout.form.iban = bank_account.iban ?? null;
            this.$refs.createBankAccountSlideout.form.vat_number = bank_account.vat_number ?? null;
            this.$refs.createBankAccountSlideout.form.currency_id = bank_account.currency_id ?? null;

            this.$refs.createBankAccountSlideout.open();
        },
    },
    watch: {
        form: {
            deep: true,
            handler: debounce(function () {
                this.$inertia.get(route('admin.bank-accounts.index'), pickBy(this.form), {preserveState: true})
            }, 500),
        },
    },
}
</script>
